import { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';

interface SpotifySlot {
  slotNumber: 1 | 2;
  refreshToken: string | null;
  displayName: string;
  spotifyId: string;
  connectedAt: any;
  connectedBy: string;
  isEmpty: boolean;
}

interface SpotifySlotData {
  slot1: SpotifySlot;
  slot2: SpotifySlot;
  loading: boolean;
  error: string | null;
}

// Spotify OAuth config
const CLIENT_ID = '5d70fa143909447bbd4669c8f633345c';
const CLIENT_SECRET = '8e22afd6abbe4cd8b50f929c769425d7';
const REDIRECT_URI = 'https://bettyvallarta.com/';

export const useSpotifySlots = () => {
  const [slots, setSlots] = useState<SpotifySlotData>({
    slot1: {
      slotNumber: 1,
      refreshToken: null,
      displayName: 'Slot 1 (Empty)',
      spotifyId: '',
      connectedAt: null,
      connectedBy: '',
      isEmpty: true,
    },
    slot2: {
      slotNumber: 2,
      refreshToken: null,
      displayName: 'Slot 2 (Empty)',
      spotifyId: '',
      connectedAt: null,
      connectedBy: '',
      isEmpty: true,
    },
    loading: true,
    error: null,
  });

  // Listen to both slots in real-time
  useEffect(() => {
    console.log('🔄 Setting up real-time listeners for Spotify slots...');

    const slot1Ref = doc(db, 'spotifySlots', 'global', 'slots', 'slot1');
    const slot2Ref = doc(db, 'spotifySlots', 'global', 'slots', 'slot2');

    const unsubscribe1 = onSnapshot(slot1Ref, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('✅ Slot 1 data received:', data.displayName);
        setSlots(prev => ({
          ...prev,
          slot1: {
            slotNumber: 1,
            refreshToken: data.refreshToken,
            displayName: data.displayName,
            spotifyId: data.spotifyId,
            connectedAt: data.connectedAt,
            connectedBy: data.connectedBy || '',
            isEmpty: false,
          },
          loading: false,
        }));
      } else {
        console.log('⚠️ Slot 1 is empty');
        setSlots(prev => ({
          ...prev,
          slot1: {
            slotNumber: 1,
            refreshToken: null,
            displayName: 'Slot 1 (Empty)',
            spotifyId: '',
            connectedAt: null,
            connectedBy: '',
            isEmpty: true,
          },
          loading: false,
        }));
      }
    });

    const unsubscribe2 = onSnapshot(slot2Ref, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('✅ Slot 2 data received:', data.displayName);
        setSlots(prev => ({
          ...prev,
          slot2: {
            slotNumber: 2,
            refreshToken: data.refreshToken,
            displayName: data.displayName,
            spotifyId: data.spotifyId,
            connectedAt: data.connectedAt,
            connectedBy: data.connectedBy || '',
            isEmpty: false,
          },
          loading: false,
        }));
      } else {
        console.log('⚠️ Slot 2 is empty');
        setSlots(prev => ({
          ...prev,
          slot2: {
            slotNumber: 2,
            refreshToken: null,
            displayName: 'Slot 2 (Empty)',
            spotifyId: '',
            connectedAt: null,
            connectedBy: '',
            isEmpty: true,
          },
          loading: false,
        }));
      }
    });

    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, []);

  // Connect Spotify to a slot
  const connectSlot = (slotNumber: 1 | 2) => {
    console.log(`🚀 Connecting Spotify to Slot ${slotNumber}...`);

    const scopes = [
      'user-read-private',
      'user-read-email',
      'user-top-read',
      'playlist-read-private',
      'playlist-read-collaborative',
      'user-library-read',
      'user-library-modify',
      'playlist-modify-public',
      'playlist-modify-private',
      'user-follow-read',
      'user-follow-modify',
    ].join(' ');

    const authUrl = `https://accounts.spotify.com/authorize?` +
      `response_type=code&` +
      `client_id=${CLIENT_ID}&` +
      `scope=${encodeURIComponent(scopes)}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `state=slot${slotNumber}&` +
      `show_dialog=true`;

    window.location.href = authUrl;
  };

  // Clear a slot (admin only)
  const clearSlot = async (slotNumber: 1 | 2) => {
    console.log(`🗑️ Clearing Slot ${slotNumber}...`);
    try {
      const slotRef = doc(db, 'spotifySlots', 'global', 'slots', `slot${slotNumber}`);
      await deleteDoc(slotRef);
      console.log(`✅ Slot ${slotNumber} cleared`);
    } catch (error) {
      console.error(`❌ Error clearing slot ${slotNumber}:`, error);
      throw error;
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const errorParam = urlParams.get('error');

      if (errorParam) {
        console.error('OAuth error:', errorParam);
        setSlots(prev => ({ ...prev, error: `Spotify authorization failed: ${errorParam}` }));
        return;
      }

      if (code && (state === 'slot1' || state === 'slot2')) {
        const slotNumber = state === 'slot1' ? 1 : 2;
        console.log(`✅ OAuth callback received for Slot ${slotNumber}`);

        setSlots(prev => ({ ...prev, loading: true }));

        try {
          // Exchange code for tokens
          const tokenResponse = await fetch('https://accounts.spotify.com/api/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Authorization': 'Basic ' + btoa(CLIENT_ID + ':' + CLIENT_SECRET),
            },
            body: `grant_type=authorization_code&code=${code}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`,
          });

          if (!tokenResponse.ok) {
            throw new Error(`Token exchange failed: ${tokenResponse.status}`);
          }

          const tokenData = await tokenResponse.json();

          if (!tokenData.refresh_token) {
            throw new Error('No refresh token received from Spotify');
          }

          // Get user profile
          const profileResponse = await fetch('https://api.spotify.com/v1/me', {
            headers: { 'Authorization': `Bearer ${tokenData.access_token}` }
          });

          if (!profileResponse.ok) {
            throw new Error('Failed to fetch Spotify profile');
          }

          const profileData = await profileResponse.json();

          // Get current user email (if available)
          const currentUserEmail = profileData.email || 'unknown';

          // Store in Firestore slot
          const slotRef = doc(db, 'spotifySlots', 'global', 'slots', `slot${slotNumber}`);
          await setDoc(slotRef, {
            refreshToken: tokenData.refresh_token,
            displayName: profileData.display_name,
            spotifyId: profileData.id,
            connectedAt: new Date(),
            connectedBy: currentUserEmail,
          });

          console.log(`🎉 Successfully stored credentials in Slot ${slotNumber}`);

          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err: any) {
          console.error('Error in OAuth callback:', err);
          setSlots(prev => ({ ...prev, error: `Setup failed: ${err.message}`, loading: false }));
        }
      }
    };

    handleCallback();
  }, []);

  return {
    slot1: slots.slot1,
    slot2: slots.slot2,
    loading: slots.loading,
    error: slots.error,
    connectSlot,
    clearSlot,
  };
};

// Hook to fetch Spotify data for both slots
export const useSpotifySlotData = (slot1: SpotifySlot, slot2: SpotifySlot) => {
  const [slot1Data, setSlot1Data] = useState<any>(null);
  const [slot2Data, setSlot2Data] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchSlotData = async (slot: SpotifySlot) => {
    if (slot.isEmpty || !slot.refreshToken) {
      return null;
    }

    try {
      console.log(`🎵 Fetching data for ${slot.displayName}...`);

      // Get fresh access token using refresh token
      const tokenResponse = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Basic ' + btoa(CLIENT_ID + ':' + CLIENT_SECRET),
        },
        body: `grant_type=refresh_token&refresh_token=${slot.refreshToken}`,
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to refresh access token');
      }

      const tokenData = await tokenResponse.json();
      const accessToken = tokenData.access_token;

      // Fetch Spotify data
      const headers = { 'Authorization': `Bearer ${accessToken}` };

      const [profile, topTracks, topArtists, playlists] = await Promise.all([
        fetch('https://api.spotify.com/v1/me', { headers }).then(r => r.json()),
        fetch('https://api.spotify.com/v1/me/top/tracks?limit=50&time_range=short_term', { headers }).then(r => r.json()),
        fetch('https://api.spotify.com/v1/me/top/artists?limit=50&time_range=short_term', { headers }).then(r => r.json()),
        fetch('https://api.spotify.com/v1/me/playlists?limit=50', { headers }).then(r => r.json()),
      ]);

      return {
        profile,
        topTracks: topTracks.items || [],
        topArtists: topArtists.items || [],
        playlists: playlists.items || [],
        accessToken,
      };
    } catch (error) {
      console.error(`❌ Error fetching data for ${slot.displayName}:`, error);
      return null;
    }
  };

  const refreshAllData = async () => {
    setLoading(true);
    const [data1, data2] = await Promise.all([
      fetchSlotData(slot1),
      fetchSlotData(slot2),
    ]);
    setSlot1Data(data1);
    setSlot2Data(data2);
    setLoading(false);
  };

  useEffect(() => {
    refreshAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot1.refreshToken, slot2.refreshToken]);

  return {
    slot1Data,
    slot2Data,
    loading,
    refreshAllData,
  };
};
